// React & Vendor Libs
const { useState, useEffect } = wp.element;

// NekoUI
import { NekoButton, NekoBlock, NekoWrapper, NekoColumn, NekoTable, useNekoTasks } from '@neko-ui';

import useDatabaseCleanerContext, { columns, columnsForEasy, modeOptions } from '../context';
import i18n from '../i18n';
import { SectionButtonStyle } from './common';
import { useTableData } from './hooks';

const sections = {
  posts: 'posts',
  postsMetadata: 'postsMetadata',
  users: 'users',
  comments: 'comments',
  transients: 'transients'
}

const SettingsWordPressCoreTables = () => {

  const { busy, busyAction, posts, postsCount, postsMetadata, postsMetadataCount, users, usersCount, mode,
    comments, commentsCount, transients, transientsCount, busyCleanBulkTasks } = useDatabaseCleanerContext();
  const { mutatePosts, setPostsCount, mutatePostsMetadata, setPostsMetadataCount, setBusyAction,
    mutateUsers, setUsersCount, mutateComments, setCommentsCount, setTransientsCount,
    mutateTransients, resetBusyBulkTasks } = useDatabaseCleanerContext();

  const bulkTasks = useNekoTasks();
  const [refreshBusy, setRefreshBusy] = useState({ posts: false, postsMetadata: false, users: false, comments: false, transients: false })
  const tableColumns = mode === modeOptions.on.value ? columns : columnsForEasy;
  const isInBusy = (key) => busyAction.some(v => v.item === key);

  useEffect(() => {
    return () => {
      if (busyCleanBulkTasks) return
      bulkTasks.stop();
      resetBusyBulkTasks();
      setBusyAction([]);
    }
  }, [busyCleanBulkTasks]);

  // Posts
  const { data: postsData, onAllRefreshClick: onPostsAllRefreshClick, onAllTrashClick: onPostsAllTrashClick, disabledAllTrash: postsDisabledAllTrash, updateList: updatePosts, updateCounts: updatePostsCount }
    = useTableData({ bulkTasks, initialList: posts, initialCounts: postsCount, mutateList: mutatePosts, onUpdateCounts: setPostsCount, section: sections.posts });
  useEffect(() => { updatePosts(posts) }, [posts, updatePosts]);
  useEffect(() => { updatePostsCount(postsCount) }, [postsCount, updatePostsCount]);

  const onPostsRefresh = async () => {
    setRefreshBusy({ ...refreshBusy, posts: true });
    await onPostsAllRefreshClick();
    setRefreshBusy({ ...refreshBusy, posts: false });
  }

  // Posts Metadata
  const { data: postsMetadataData, onAllRefreshClick: onPostsMetadataAllRefreshClick, onAllTrashClick: onPostsMetadataAllTrashClick, disabledAllTrash: postsMetadataDisabledAllTrash, updateList: updatePostsMetadata, updateCounts: updatePostsMetadataCount }
    = useTableData({ bulkTasks, initialList: postsMetadata, initialCounts: postsMetadataCount, mutateList: mutatePostsMetadata, onUpdateCounts: setPostsMetadataCount, section: sections.postsMetadata });
  useEffect(() => { updatePostsMetadata(postsMetadata) }, [postsMetadata, updatePostsMetadata]);
  useEffect(() => { updatePostsMetadataCount(postsMetadataCount) }, [postsMetadataCount, updatePostsMetadataCount]);

  const onPostsMetadataRefresh = async () => {
    setRefreshBusy({ ...refreshBusy, postsMetadata: true });
    await onPostsMetadataAllRefreshClick();
    setRefreshBusy({ ...refreshBusy, postsMetadata: false });
  }

  // Users
  const { data: usersData, onAllRefreshClick: onUsersAllRefreshClick, onAllTrashClick: onUsersAllTrashClick,
    disabledAllTrash: usersDisabledAllTrash, updateList: updateUsers, updateCounts: updateUsersCount }
    = useTableData({ bulkTasks, initialList: users, initialCounts: usersCount, mutateList: mutateUsers, onUpdateCounts: setUsersCount, section: sections.users });
  useEffect(() => { updateUsers(users) }, [users, updateUsers]);
  useEffect(() => { updateUsersCount(usersCount) }, [usersCount, updateUsersCount]);

  const onUsersRefresh = async () => {
    setRefreshBusy({ ...refreshBusy, users: true });
    await onUsersAllRefreshClick();
    setRefreshBusy({ ...refreshBusy, users: false });
  }

  // Comments
  const { data: commentsData, onAllRefreshClick: onCommentsAllRefreshClick, onAllTrashClick: onCommentsAllTrashClick,
    disabledAllTrash: commentsDisabledAllTrash, updateList: updateComments, updateCounts: updateCommentsCount }
    = useTableData({ bulkTasks, initialList: comments, initialCounts: commentsCount, mutateList: mutateComments, onUpdateCounts: setCommentsCount, section: sections.comments });
  useEffect(() => { updateComments(comments) }, [comments, updateComments]);
  useEffect(() => { updateCommentsCount(commentsCount) }, [commentsCount, updateCommentsCount]);

  const onCommentsRefresh = async () => {
    setRefreshBusy({ ...refreshBusy, comments: true });
    await onCommentsAllRefreshClick();
    setRefreshBusy({ ...refreshBusy, comments: false });
  }

  // Transients
  const { data, onAllRefreshClick: onTransientsAllRefreshClick, onAllTrashClick: onTransientsTrashClick,
    disabledAllTrash: transientsDisabledAllTrash, updateList: updateTransients, updateCounts: updateTransientsCount }
    = useTableData({ bulkTasks, initialList: transients, initialCounts: transientsCount, mutateList: mutateTransients, onUpdateCounts: setTransientsCount, section: sections.transients });
  useEffect(() => { updateTransients(transients) }, [transients, updateTransients]);
  useEffect(() => { updateTransientsCount(transientsCount) }, [transientsCount, updateTransientsCount]);

  const onTransientsRefresh = async () => {
    setRefreshBusy({ ...refreshBusy, transients: true });
    await onTransientsAllRefreshClick();
    setRefreshBusy({ ...refreshBusy, transients: false });
  }

  return (
    <NekoWrapper>

      <NekoColumn minimal>
        <NekoBlock busy={busy || refreshBusy.posts || isInBusy(sections.posts)} title="Posts" className="primary"
          action={
            <div style={{ display: 'flex' }}>
              <NekoButton icon="replay" title={i18n.INFO_BULK_REFRESH} 
                style={SectionButtonStyle} onClick={onPostsRefresh} spinning={refreshBusy.posts} isBusy={refreshBusy.posts} />
              <NekoButton icon="trash" title={i18n.INFO_BULK_DELETE}
                style={SectionButtonStyle}
                onClick={() => onPostsAllTrashClick()} disabled={postsDisabledAllTrash} />
              {/* <NekoButton icon="cog" style={SectionButtonStyle} onClick={() => onAllRefreshClick()}>Options</NekoButton> */}
            </div>
          }>
          <NekoTable variant="raw" data={postsData} columns={tableColumns} selectedItems={[]} />
        </NekoBlock>
        <NekoBlock busy={busy || refreshBusy.postsMetadata || isInBusy(sections.postsMetadata)} title="Posts Metadata" className="primary"
          action={
            <div style={{ display: 'flex' }}>
              <NekoButton icon="replay" title={i18n.INFO_BULK_REFRESH} 
                style={SectionButtonStyle} onClick={onPostsMetadataRefresh} spinning={refreshBusy.postsMetadata} isBusy={refreshBusy.postsMetadata} />
              <NekoButton icon="trash" title={i18n.INFO_BULK_DELETE}
                style={SectionButtonStyle} onClick={() => onPostsMetadataAllTrashClick()} disabled={postsMetadataDisabledAllTrash} />
            </div>
          }>
          <NekoTable variant="raw" data={postsMetadataData} columns={tableColumns} selectedItems={[]} />
        </NekoBlock>
      </NekoColumn>

      <NekoColumn minimal>
        <NekoBlock busy={busy || refreshBusy.users || isInBusy(sections.users)} title="Users" className="primary"
          action={
            <div style={{ display: 'flex' }}>
              <NekoButton icon="replay" title={i18n.INFO_BULK_REFRESH} 
                style={SectionButtonStyle} onClick={onUsersRefresh} spinning={refreshBusy.users} isBusy={refreshBusy.users} />
              <NekoButton icon="trash" title={i18n.INFO_BULK_DELETE}
                style={SectionButtonStyle} onClick={() => onUsersAllTrashClick()} disabled={usersDisabledAllTrash} />
            </div>
          }>
          <NekoTable variant="raw" data={usersData} columns={tableColumns} selectedItems={[]} />
        </NekoBlock>
        <NekoBlock busy={busy || refreshBusy.comments || isInBusy(sections.comments)} title="Comments" className="primary"
          action={
            <div style={{ display: 'flex' }}>
              <NekoButton icon="replay" title={i18n.INFO_BULK_REFRESH} 
                style={SectionButtonStyle} onClick={onCommentsRefresh} spinning={refreshBusy.comments} isBusy={refreshBusy.comments} />
              <NekoButton icon="trash" title={i18n.INFO_BULK_DELETE}
                style={SectionButtonStyle} onClick={() => onCommentsAllTrashClick()} disabled={commentsDisabledAllTrash} />
            </div>
          }>
          <NekoTable variant="raw" data={commentsData} columns={tableColumns} selectedItems={[]} />
        </NekoBlock>

        <NekoBlock busy={busy || refreshBusy.transients || isInBusy(sections.transients)} title="Transients" className="primary"
          action={
            <div style={{ display: 'flex' }}>
            <NekoButton icon="replay" title={i18n.INFO_BULK_REFRESH} 
              style={SectionButtonStyle} onClick={onTransientsRefresh} spinning={refreshBusy.transients} isBusy={refreshBusy.transients} />
            <NekoButton icon="trash" title={i18n.INFO_BULK_DELETE}
              style={SectionButtonStyle} onClick={() => onTransientsTrashClick()} disabled={transientsDisabledAllTrash} />
            </div>
          }>
          <NekoTable variant="raw" data={data} columns={tableColumns} selectedItems={[]} />
        </NekoBlock>

      </NekoColumn>

    </NekoWrapper>
  );
};

export default SettingsWordPressCoreTables;